/**
 * 下载回退实现（Firefox / Safari）。见 types.ts 的总注释。
 *
 * 这两个浏览器没有 File System Access API（§2.6 的矩阵），能做的只有：
 * 进 —— `<input type=file>` / 拖拽；出 —— `<a download>` 触发一次下载。
 * 没有句柄概念，`save()` 永远返回 null，UI 文案相应是「下载副本…」（ADR-0016）。
 */
import {
  throwIfAborted,
  type FileGateway,
  type FileHandleRef,
  type GatewayCallOptions,
  type OpenedFile,
  type SavePayload,
} from './types';

/**
 * 与 DOM 打交道的部分抽成驱动注入：测试不需要真实 DOM，
 * 实现也不用关心 `<input>` 元素的生命周期细节。
 */
export interface DownloadDriver {
  /** 弹出文件选择。用户取消时返回空数组（`<input type=file>` 的取消没有专门事件）。 */
  pickFiles(
    accept: readonly string[],
  ): Promise<ReadonlyArray<{ readonly name: string; readonly bytes: Uint8Array }>>;
  /** 触发一次浏览器下载。 */
  download(name: string, bytes: Uint8Array, mimeType: string): void;
}

export class DownloadGateway implements FileGateway {
  readonly kind = 'download' as const;
  readonly canOverwrite = false;

  constructor(private readonly driver: DownloadDriver) {}

  async open(accept: readonly string[], options?: GatewayCallOptions): Promise<OpenedFile[]> {
    throwIfAborted(options?.signal);
    const picked = await this.driver.pickFiles(accept);
    throwIfAborted(options?.signal);
    return picked.map((file) => ({ name: file.name, bytes: file.bytes, handle: null }));
  }

  async save(
    payload: SavePayload,
    _handle?: FileHandleRef | null,
    options?: GatewayCallOptions,
  ): Promise<FileHandleRef | null> {
    // handle 被刻意忽略：下载模式没有"原来那个文件"可言
    throwIfAborted(options?.signal);
    this.driver.download(payload.name, payload.bytes, payload.mimeType ?? 'application/octet-stream');
    return null;
  }
}

/**
 * 真实 DOM 驱动。挂在浏览器环境用；单元测试用假驱动。
 *
 * DOM 的获取推迟到方法被调用时：探测阶段（selectGateway）可能在没有 DOM 的
 * 环境里执行，只有用户真去打开/保存时才需要 document 存在。
 */
export function domDownloadDriver(doc?: Document): DownloadDriver {
  const requireDoc = (): Document => {
    const resolved = doc ?? (globalThis as { document?: Document }).document;
    if (resolved === undefined) {
      throw new Error('domDownloadDriver 需要 DOM 环境；无 DOM 场景请注入自定义 DownloadDriver');
    }
    return resolved;
  };
  return {
    pickFiles(accept) {
      return new Promise((resolve, reject) => {
        const input = requireDoc().createElement('input');
        input.type = 'file';
        input.multiple = true;
        if (accept.length > 0) input.accept = accept.join(',');

        input.addEventListener('change', () => {
          const files = [...(input.files ?? [])];
          Promise.all(
            files.map(async (file) => ({
              name: file.name,
              bytes: new Uint8Array(await file.arrayBuffer()),
            })),
          ).then(resolve, reject);
        });
        // 现代浏览器在用户关闭选择框时派发 cancel；老浏览器没有这个事件，
        // 表现为 Promise 一直挂着——这与"用户没做选择"语义一致，可接受
        input.addEventListener('cancel', () => resolve([]));
        input.click();
      });
    },
    download(name, bytes, mimeType) {
      const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: mimeType }));
      try {
        const anchor = requireDoc().createElement('a');
        anchor.href = url;
        anchor.download = name;
        anchor.click();
      } finally {
        // 撤销要放到下一轮事件循环：同步撤销在部分浏览器上会取消下载
        setTimeout(() => URL.revokeObjectURL(url), 0);
      }
    },
  };
}
